import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline'
import parse from 'html-react-parser'
import { useMemo } from 'react'
import { Property, PropertyAnswer } from '../../../gql/graphql'
import { classNames } from '../../_helpers'
import { MainHeader } from '../../_ui/MainHeader'
import { EditUserForm } from '../EditUser/EditUserForm'
import { useMyself } from '../UserContext'
import { EditMembershipFieldView } from './EditMembershipFieldView'

type PropTypes = {
  title: string,
  description: string,
  status: string,
  answers: PropertyAnswer[]
  fields: Property[]
  submitAction?: () => void
  formData: any
  handleCancelMembership: () => void
  changeFormFieldValue: (propertyID: string, value: boolean | string | string[]) => void
  hideHeader?: boolean
  hidePrivate?: boolean
  includeStatus?: boolean
  submitOnClick?: boolean
  readonly?: boolean
  showInfotext?: boolean
  showNotificationPermissions?: boolean
  footnote?: string
}

export const EditMembershipView = ({ status, readonly, title, description, answers, fields, submitAction, handleCancelMembership, changeFormFieldValue, formData, hidePrivate, hideHeader, includeStatus, submitOnClick, showInfotext, showNotificationPermissions, footnote}: PropTypes) => {
  const user = useMyself()
  const visibleFields = useMemo(
    () => fields.filter(property => property && (!property.isPrivate || !hidePrivate)),
    [fields, hidePrivate]
  )
  const hasContent = visibleFields.length > 0 || !!footnote || (!!showNotificationPermissions && !!user)

  return (
    <form className="content space-y-8" onSubmit={e => {
      e.preventDefault();
      if (submitAction) {
        submitAction()
      }
    }}>
      <div className="space-y-8 sm:space-y-5">
        <div className="space-y-6 sm:space-y-5">
          {!hideHeader && (
            <div className={'rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6'}>
              <MainHeader
                title={title}
              />
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <div className={'prose max-w-full text-sm text-gray-700'}>
                    {parse(description)}
                  </div>
                  <div className={'mt-4 flex flex-wrap items-center gap-y-2 border-t border-gray-100 pt-4 text-gray-900'}>
                    <span className={'inline-block mr-2 font-medium'}>Mitgliedschaftsstatus:</span>

                    {status === 'requested' && (
                      <span className="inline-flex items-center rounded-md bg-yellow-50 mr-2 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                        Angefragt
                      </span>
                    )}
                    {status === 'active' && (
                      <span className="inline-flex items-center rounded-md bg-green-50 mr-2 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        Aktiv
                      </span>
                    )}

                    {!readonly && (
                      <button
                        onClick={handleCancelMembership}
                        className="inline-flex items-center rounded-md bg-red-50 mr-2 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/40"
                      >
                        <ArrowRightStartOnRectangleIcon className={'w-4 h-4 mr-1'} />
                        Gruppe verlassen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {hasContent && (
            <div className={classNames(
              "space-y-6",
              !hideHeader ? 'rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6' : ''
            )}>
              {visibleFields.map(property => (
                <div key={property.id}>
                  <EditMembershipFieldView
                    property={property}
                    answer={formData[property.id]?.value}
                    changeAnswer={value => changeFormFieldValue(property.id, value)}
                    submitOnClick={submitOnClick}
                    readonly={readonly || property.isReadonly || false}
                    showInfotext={showInfotext}
                  />
                </div>
              ))}

              {showNotificationPermissions && user && (
                <div className={'border-t border-gray-200 pt-6'}>
                  <div className={'max-w-xl'}>
                    <EditUserForm
                      user={user}
                      onChange={changeFormFieldValue}
                      hidePersonalDataSettings
                    />
                  </div>
                </div>
              )}

              {footnote && (
                <div className={'max-w-xl text-sm text-gray-600'}>
                  <div>
                    {parse(footnote)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {submitAction && (
        <div className="flex max-w-xl justify-end border-t border-gray-200 pt-5">
          <button
            type="submit"
            className="inline-flex justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Speichern
          </button>
        </div>
      )}
    </form>
  )
}
